"use client";

import { createBrowserClient } from "@supabase/ssr";
import { supabaseAnonKey, supabaseUrl } from "@/lib/supabase/config";
import {
  ADMIN_AUTH_STORAGE_KEY,
  PLATFORM_AUTH_STORAGE_KEY,
  scopeForPath,
  type AuthScope,
} from "@/lib/supabase/scope";

type BrowserClient = ReturnType<typeof createBrowserClient>;

// Singleton PER-SCOPE: satu instance untuk sesi storefront, satu untuk sesi
// admin. Keduanya memakai cookie/storageKey berbeda sehingga bisa login admin
// dan pembeli sekaligus tanpa saling menimpa. createBrowserSupabase dipakai
// ~96x di banyak file; membuat client baru tiap pemanggilan menelurkan banyak
// GoTrueClient dengan storageKey sama ("Multiple GoTrueClient instances") dan
// memperburuk perebutan navigator.locks auth-token lock.
const cachedClients: Partial<Record<AuthScope, BrowserClient>> = {};

/**
 * Pilih scope: gunakan argumen eksplisit bila ada, jika tidak deteksi dari
 * URL saat ini. Ini membuat helper bersama (mis. `data.ts`) otomatis memakai
 * sesi yang benar — sesi admin saat dipanggil dari halaman `/admin`, sesi
 * pembeli saat dari storefront — tanpa perlu mengubah ratusan call site.
 */
function resolveScope(scope?: AuthScope): AuthScope {
  if (scope) return scope;
  if (typeof window === "undefined") return "store";
  return scopeForPath(window.location.pathname);
}

export function createBrowserSupabase(scope?: AuthScope) {
  if (!supabaseUrl || !supabaseAnonKey) {
    return null;
  }

  const resolved = resolveScope(scope);

  const existing = cachedClients[resolved];
  if (existing) return existing;

  const client = createBrowserClient(supabaseUrl, supabaseAnonKey, {
    // PENTING: matikan singleton bawaan @supabase/ssr. Ia meng-cache SATU
    // client global tanpa peduli cookieOptions, sehingga permintaan client
    // scope kedua akan keliru mengembalikan client scope pertama. Kita kelola
    // cache per-scope sendiri di `cachedClients`.
    isSingleton: false,
    // Sesi admin & sesi platform pakai nama cookie berbeda agar terpisah dari
    // sesi pembeli (dan satu sama lain). Storefront pakai default supabase
    // (tanpa override) supaya sesi pembeli yang sudah ada tidak ter-logout.
    ...(resolved === "admin"
      ? { cookieOptions: { name: ADMIN_AUTH_STORAGE_KEY } }
      : resolved === "platform"
        ? { cookieOptions: { name: PLATFORM_AUTH_STORAGE_KEY } }
        : {}),
  });

  cachedClients[resolved] = client;
  return client;
}
